/**
 * 护理方案卡片组件 (CareSop Card)
 * 显示用户护理方案的完整信息，支持步骤交互
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Circle, Droplets, Calendar, Pill } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  getUnifiedCareSop,
  getWaterType,
  getDiseaseName,
} from "@/lib/careSopAdapter";
import type { CareSopInstance, CareSopInstanceStep } from "@/types/care-sop";

interface CareSopCardProps {
  careSop: CareSopInstance;
  onToggleStep: (stepIndex: number, isCompleted: boolean) => void;
  onComplete?: () => void;
  onAbandon?: () => void;
  isToggling?: boolean;
  className?: string;
}

/**
 * 护理方案卡片组件
 */
export function CareSopCard({
  careSop,
  onToggleStep,
  onComplete,
  onAbandon,
  isToggling = false,
  className,
}: CareSopCardProps) {
  const { t } = useTranslation("common");

  // 统一处理不同的数据结构
  const unifiedCareSop = getUnifiedCareSop(careSop);
  const waterType = getWaterType(careSop);
  const diseaseName = getDiseaseName(careSop);
  const isMarine = waterType === "saltwater";

  // 计算进度
  const total = unifiedCareSop.steps.length;
  const completed = unifiedCareSop.steps.filter((s) => s.is_completed).length;
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <Card
      className={cn(
        "overflow-hidden border-l-4",
        isMarine ? "border-l-blue-500" : "border-l-green-500",
        className,
      )}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <Pill className="h-5 w-5" />
            推荐护理方案
          </CardTitle>
          <Badge
            variant={isMarine ? "default" : "secondary"}
            className={cn(
              "flex items-center gap-1",
              isMarine && "bg-blue-500 text-white",
            )}
          >
            <Droplets className="h-3 w-3" />
            {isMarine ? "海水方案" : "淡水方案"}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* 进度条 */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">执行进度</span>
            <span className="font-medium">
              {completed} / {total} ({progress}%)
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <Separator />

        {/* 步骤列表 */}
        <div className="space-y-3">
          {unifiedCareSop.steps.map((step, index) => (
            <StepItem
              key={index}
              step={step}
              index={index}
              onToggle={onToggleStep}
              disabled={isToggling}
            />
          ))}
        </div>

        {/* 操作按钮 */}
        {(onComplete || onAbandon) && unifiedCareSop.status === "active" && (
          <>
            <Separator />
            <div className="flex gap-2 pt-2">
              {onComplete && (
                <Button
                  variant="outline"
                  onClick={onComplete}
                  className="flex-1"
                  disabled={completed === 0}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  标记完成
                </Button>
              )}
              {onAbandon && (
                <Button variant="destructive" onClick={onAbandon}>
                  放弃方案
                </Button>
              )}
            </div>
          </>
        )}

        {/* 元信息 */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            创建于{" "}
            {new Date(unifiedCareSop.created_at).toLocaleDateString("zh-CN")}
          </span>
          {diseaseName && (
            <Badge variant="outline" className="text-xs">
              {diseaseName}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * 步骤单项组件
 */
interface StepItemProps {
  step: CareSopInstanceStep;
  index: number;
  onToggle: (stepIndex: number, isCompleted: boolean) => void;
  disabled?: boolean;
}

function StepItem({ step, index, onToggle, disabled }: StepItemProps) {
  return (
    <div
      className={cn(
        "p-3 rounded-lg border transition-all",
        step.is_completed
          ? "bg-muted/50 opacity-70"
          : "bg-card hover:bg-muted/30",
      )}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={step.is_completed}
          onCheckedChange={(checked) => onToggle(index, checked === true)}
          disabled={disabled}
          className="mt-1 flex-shrink-0"
        />

        <div className="flex-1 min-w-0">
          {/* 步骤标签 */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {step.phase_label && (
              <Badge variant="outline" className="text-xs">
                {step.phase_label}
              </Badge>
            )}
            {(step.offset_day !== undefined ||
              step.offset_start !== undefined) && (
              <Badge variant="secondary" className="text-xs">
                Day{" "}
                {step.offset_day !== undefined
                  ? step.offset_day
                  : step.offset_start}
                {step.offset_end !== undefined &&
                  step.offset_end !== (step.offset_day ?? step.offset_start) &&
                  ` - ${step.offset_end}`}
              </Badge>
            )}
          </div>

          {/* 操作描述 - 直接渲染后端计算好的文案 */}
          <p className="font-medium text-sm">{step.action}</p>

          {/* 产品信息 */}
          {step.product_name && (
            <p className="text-xs text-muted-foreground mt-1">
              使用物品: {step.product_name}
            </p>
          )}

          {/* 剂量信息 */}
          {(step.dosage || step.duration) && (
            <div className="flex items-center gap-3 mt-2 text-xs">
              {step.dosage && (
                <span className="text-primary font-medium">
                  {step.dosage}
                  {step.dosage_unit && ` ${step.dosage_unit}`}
                </span>
              )}
              {step.duration && (
                <span className="text-muted-foreground">
                  持续: {step.duration}
                </span>
              )}
            </div>
          )}

          {/* 注意事项 */}
          {step.notes && (
            <p className="text-xs text-muted-foreground mt-2 italic">
              {step.notes}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 简化版卡片（用于紧凑展示）
 */
interface CareSopCardCompactProps {
  careSop: CareSopInstance;
  onToggleStep?: (stepIndex: number, isCompleted: boolean) => void;
  className?: string;
}

export function CareSopCardCompact({
  careSop,
  onToggleStep,
  className,
}: CareSopCardCompactProps) {
  // 统一处理不同的数据结构
  const unifiedCareSop = getUnifiedCareSop(careSop);
  const waterType = getWaterType(careSop);
  const diseaseName = getDiseaseName(careSop);
  const isMarine = waterType === "saltwater";
  const total = unifiedCareSop.steps.length;
  const completed = unifiedCareSop.steps.filter((s) => s.is_completed).length;
  const progress = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <Card
      className={cn(
        "border-l-4",
        isMarine ? "border-l-blue-500" : "border-l-green-500",
        className,
      )}
    >
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge variant={isMarine ? "default" : "secondary"}>
              {isMarine ? "🌊 海水" : "💧 淡水"}
            </Badge>
            {diseaseName && (
              <span className="font-medium text-sm">{diseaseName}</span>
            )}
          </div>
          <span className="text-sm text-muted-foreground">
            {completed}/{total} 步骤
          </span>
        </div>
        <Progress value={progress} className="h-1.5" />
      </CardContent>
    </Card>
  );
}

export default CareSopCard;
